import { useState, useEffect } from 'react'
import { Loader2, MapPin, Plus, Trash2, X } from 'lucide-react'
import { adminApi } from '../../lib/api'
import { LocationPicker, type LocationValue } from '../maps/LocationPicker'
import { toNumericCoordinate } from '../../utils/location'
import { useToast } from '../ui/ToastProvider'

const parseLocationValue = (raw: any) => {
  if (!raw) return null

  let location = raw
  if (typeof raw === 'string') {
    try {
      location = JSON.parse(raw)
    } catch {
      return {
        id: undefined,
        name: raw,
        address: raw,
        latitude: null,
        longitude: null,
      }
    }
  }

  if (typeof location !== 'object' || location === null) {
    return null
  }

  const latitude = toNumericCoordinate(location.latitude ?? location.lat)
  const longitude = toNumericCoordinate(location.longitude ?? location.lng)

  return {
    id: location.id,
    name: location.name ?? location.address ?? location.formatted_address ?? 'Unnamed location',
    address: location.address ?? location.formatted_address ?? '',
    latitude,
    longitude,
  }
}

interface TripLocation {
  id: string
  name: string
  address?: string
  latitude: number
  longitude: number
  source: 'manual' | 'event'
  eventId?: string
  eventTitle?: string
}

interface LocationTabProps {
  tripId?: string
  isNewTrip: boolean
}

export default function LocationTab({ tripId, isNewTrip }: LocationTabProps) {
  const { showToast } = useToast()
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [tripLocations, setTripLocations] = useState<TripLocation[]>([])
  const [loading, setLoading] = useState(false)
  const [showAddLocation, setShowAddLocation] = useState(false)
  const [newLocation, setNewLocation] = useState<LocationValue>({
    address: '',
    lat: null,
    lng: null,
  })
  const [addingLocation, setAddingLocation] = useState(false)

  // Load all locations (from trip and events)
  useEffect(() => {
    if (!tripId || isNewTrip) return

    const loadLocations = async () => {
      setLoading(true)
      try {
        // Load trip locations
        const locationsResponse = await adminApi.getTripLocations(tripId)
        const tripLocationsPayload = locationsResponse.data?.data || locationsResponse.data || []
        const rawTripLocations = Array.isArray(tripLocationsPayload) ? (tripLocationsPayload as any[]) : []
        const tripLocs = rawTripLocations.reduce<TripLocation[]>(
          (acc, loc: any) => {
            const latitude = toNumericCoordinate(loc.latitude)
            const longitude = toNumericCoordinate(loc.longitude)
            if (latitude === null || longitude === null) {
              return acc
            }
            acc.push({
              id: loc.id,
              name: loc.name || loc.address || 'Trip location',
              address: loc.address ?? undefined,
              latitude,
              longitude,
              source: 'manual',
            })
            return acc
          },
          []
        )

        // Load event locations
        const eventsResponse = await adminApi.getTripEvents(tripId)
        const eventsPayload = eventsResponse.data?.data ?? eventsResponse.data ?? []
        const rawEvents = Array.isArray(eventsPayload)
          ? eventsPayload
          : Array.isArray(eventsPayload.events)
            ? eventsPayload.events
            : []

        const eventLocs = (rawEvents as any[]).reduce<TripLocation[]>((acc, event: any) => {
          const parsedLocation = parseLocationValue(event.location || event.location_json || event.locationJson)
          if (!parsedLocation || parsedLocation.latitude === null || parsedLocation.longitude === null) {
            return acc
          }
          acc.push({
            id: parsedLocation.id || `event-${event.id}`,
            name: parsedLocation.name || 'Event location',
            address: parsedLocation.address ?? undefined,
            latitude: parsedLocation.latitude,
            longitude: parsedLocation.longitude,
            source: 'event',
            eventId: event.id,
            eventTitle: event.title,
          })
          return acc
        }, [])

        // Merge and deduplicate by coordinates (within 0.0001 degree precision)
        const allLocations = [...tripLocs, ...eventLocs]
        const uniqueLocations = allLocations.reduce((acc: TripLocation[], loc) => {
          const isDuplicate = acc.some(
            (existingLoc) =>
              Math.abs(existingLoc.latitude - loc.latitude) < 0.0001 &&
              Math.abs(existingLoc.longitude - loc.longitude) < 0.0001
          )
          if (!isDuplicate) {
            acc.push(loc)
          }
          return acc
        }, [])

        setTripLocations(uniqueLocations)
      } catch (error) {
        console.error('Failed to load locations:', error)
      } finally {
        setLoading(false)
      }
    }

    loadLocations()
  }, [tripId, isNewTrip])

  const handleAddLocation = async () => {
    if (!tripId || !newLocation.lat || !newLocation.lng) return

    setAddingLocation(true)
    try {
      const locationData: any = {
        name: newLocation.address || `${newLocation.lat.toFixed(4)}, ${newLocation.lng.toFixed(4)}`,
        latitude: Number(newLocation.lat),
        longitude: Number(newLocation.lng),
      }

      // Only add address if it exists and is non-empty
      if (newLocation.address && newLocation.address.trim()) {
        locationData.address = newLocation.address.trim()
      }

      console.log('🟢 Adding trip location with data:', JSON.stringify(locationData, null, 2))
      const response = await adminApi.addTripLocation(tripId, locationData)
      console.log('✅ Add location response:', response)
      
      // Reload locations
      const locationsResponse = await adminApi.getTripLocations(tripId)
      const tripLocationsPayload = locationsResponse.data?.data || locationsResponse.data || []
      const rawTripLocations = Array.isArray(tripLocationsPayload) ? (tripLocationsPayload as any[]) : []
      const tripLocs = rawTripLocations.reduce<TripLocation[]>(
        (acc, loc: any) => {
          const latitude = toNumericCoordinate(loc.latitude)
          const longitude = toNumericCoordinate(loc.longitude)
          if (latitude === null || longitude === null) {
            return acc
          }
          acc.push({
            id: loc.id,
            name: loc.name || loc.address || 'Trip location',
            address: loc.address ?? undefined,
            latitude,
            longitude,
            source: 'manual',
          })
          return acc
        },
        []
      )

      // Keep event locations
      const eventLocs = tripLocations.filter(loc => loc.source === 'event')
      setTripLocations([...tripLocs, ...eventLocs])
      
      setNewLocation({ address: '', lat: null, lng: null })
      setShowAddLocation(false)
      setStatusMessage('✅ Location added successfully.')
      showToast('Location added successfully!', { type: 'success' })
    } catch (error) {
      console.error('Failed to add location:', error)
      setStatusMessage('Failed to add location. Please try again.')
    } finally {
      setAddingLocation(false)
    }
  }

  const handleRemoveLocation = async (locationId: string, source: string) => {
    if (!tripId || source === 'event') return
    
    if (!confirm('Are you sure you want to remove this location?')) return
    
    try {
      await adminApi.removeTripLocation(tripId, locationId)
      setTripLocations(prev => prev.filter(loc => loc.id !== locationId))
      setStatusMessage('✅ Location removed successfully.')
      showToast('Location removed successfully!', { type: 'success' })
    } catch (error) {
      console.error('Failed to remove location:', error)
      setStatusMessage('Failed to remove location. Please try again.')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Locations</h3>
          <p className="text-sm text-gray-600 mt-1">
            Manage locations from events and add custom locations for future events
          </p>
        </div>
        <button
          onClick={() => setShowAddLocation(!showAddLocation)}
          className="btn-secondary flex items-center gap-2"
          disabled={isNewTrip}
        >
          <Plus className="h-4 w-4" />
          Add Location
        </button>
      </div>

      {statusMessage && (
        <div
          className={`text-sm rounded-md border px-3 py-2 ${
            statusMessage.toLowerCase().includes('failed')
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-green-200 bg-green-50 text-green-700'
          }`}
        >
          {statusMessage}
        </div>
      )}

      {/* Add Location Form */}
      {showAddLocation && !isNewTrip && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-start justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900">Add New Location</h4>
            <button
              onClick={() => {
                setShowAddLocation(false)
                setNewLocation({ address: '', lat: null, lng: null })
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <LocationPicker
            label=""
            description="Search for a location or click on the map to add it. This location can be used when creating events."
            value={newLocation}
            onChange={setNewLocation}
          />
          
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setShowAddLocation(false)
                setNewLocation({ address: '', lat: null, lng: null })
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAddLocation}
              className="btn-primary flex items-center gap-2"
              disabled={!newLocation.lat || !newLocation.lng || addingLocation}
            >
              {addingLocation && <Loader2 className="h-4 w-4 animate-spin" />}
              {addingLocation ? 'Adding...' : 'Add Location'}
            </button>
          </div>
        </div>
      )}

      {/* All Locations List */}
      {isNewTrip ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
          <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-base font-medium">Save the trip first</p>
          <p className="text-sm text-gray-400 mt-1">You can add and manage locations after creating the trip</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading locations...
        </div>
      ) : tripLocations.length > 0 ? (
        <div className="space-y-2">
          {tripLocations.map((loc) => (
            <div key={loc.id} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors shadow-sm">
              <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {loc.name}
                </p>
                {loc.address && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {loc.address}
                  </p>
                )}
                {loc.source === 'event' && loc.eventTitle && (
                  <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
                    <span>📍</span>
                    <span>From event: {loc.eventTitle}</span>
                  </p>
                )}
                {loc.source === 'manual' && (
                  <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                    <span>✓</span>
                    <span>Available for events</span>
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {loc.latitude.toFixed(6)}, {loc.longitude.toFixed(6)}
                </p>
              </div>
              {loc.source === 'manual' && (
                <button
                  onClick={() => handleRemoveLocation(loc.id, loc.source)}
                  className="text-red-400 hover:text-red-600 transition-colors p-1.5 hover:bg-red-50 rounded"
                  title="Remove location"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
          <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-base font-medium">No locations yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Add locations manually or they will appear automatically from events
          </p>
          <button
            onClick={() => setShowAddLocation(true)}
            className="mt-4 btn-primary flex items-center gap-2 mx-auto"
          >
            <Plus className="h-4 w-4" />
            Add First Location
          </button>
        </div>
      )}
    </div>
  )
}
